interface AITranslationRequest {
  text: string;
  targetLanguage: string;
  targetLanguageCode?: string;
  sourceLanguage?: string;
}

interface AITranslationResponse {
  translatedText: string;
  sourceLanguage?: string;
  targetLanguage: string;
  confidence?: number;
}

interface BulkAITranslationRequest {
  texts: string[];
  targetLanguage: string;
  targetLanguageCode?: string;
  sourceLanguage?: string;
}

interface BulkAITranslationResponse {
  translations: AITranslationResponse[];
  totalProcessed: number;
  errors: { index: number; error: string }[];
}

declare global {
  interface Window {
    APP_CONFIG?: {
      OPENAI_API_KEY?: string;
    };
  }
}

class AITranslationService {
  private getApiKey(): string {
    return window.APP_CONFIG?.OPENAI_API_KEY || process.env.REACT_APP_OPENAI_API_KEY || '';
  }

  async translateText(request: AITranslationRequest): Promise<AITranslationResponse> {
    if (!this.getApiKey()) {
      throw new Error('AI translation service not configured');
    }

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getApiKey()}`,
        },
        body: JSON.stringify({
          model: this.isJsonText(request.text) ? 'gpt-4' : 'gpt-3.5-turbo', // Use GPT-4 for JSON for better accuracy
          messages: [
            {
              role: 'system',
              content: `You are a professional translator. ${this.isJsonText(request.text) ? this.getJsonInstructions() + ' ' : ''}Translate the given text to ${request.targetLanguage}. ${this.getLanguageInstructions(request.targetLanguageCode || '')} Return ONLY the translated result, no explanations.`
            },
            ...(this.isJsonText(request.text) ? [
              {
                role: 'user',
                content: 'Translate this JSON to Uzbek: {"hello": "Hello"}'
              },
              {
                role: 'assistant',
                content: '{"hello": "Salom"}'
              }
            ] : []),
            {
              role: 'user',
              content: this.isJsonText(request.text) ? request.text.replace(/\s+/g, ' ').trim() : request.text
            }
          ],
          max_tokens: 1000,
          temperature: this.isJsonText(request.text) ? 0.1 : 0.3, // Lower temperature for JSON to be more precise
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Ошибка аутентификации OpenAI API: проверьте API ключ в настройках. Код ошибки: 401');
        }
        throw new Error(`AI Translation API error: ${response.status}`);
      }

      const data = await response.json();
      const translatedText = data.choices?.[0]?.message?.content?.trim() || '';

      return {
        translatedText,
        sourceLanguage: request.sourceLanguage || 'auto-detected',
        targetLanguage: request.targetLanguage,
        confidence: 0.9, // Mock confidence score
      };
    } catch (error) {
      console.error('AI Translation error:', error);
      throw new Error(`Failed to translate text: ${error}`);
    }
  }

  async translateBulk(request: BulkAITranslationRequest): Promise<BulkAITranslationResponse> {
    if (!this.getApiKey()) {
      throw new Error('AI translation service not configured');
    }

    const results: AITranslationResponse[] = [];
    const errors: { index: number; error: string }[] = [];

    // For large batches, use optimized batch processing
    const batchSize = Math.min(50, request.texts.length); // Process up to 50 texts at once
    
    for (let i = 0; i < request.texts.length; i += batchSize) {
      const batch = request.texts.slice(i, i + batchSize);
      
      try {
        // Create batch prompt for multiple translations
        const batchPrompt = batch.map((text, index) => {
          // For JSON, ensure it's on one line
          const cleanText = this.isJsonText(text) ? text.replace(/\s+/g, ' ').trim() : text;
          return `${i + index + 1}. ${cleanText}`;
        }).join('\n');
        
        // Check if any text in batch is JSON
        const hasJsonInBatch = batch.some(text => this.isJsonText(text));

        const response = await fetch('https://api.openai.com/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.getApiKey()}`,
          },
          body: JSON.stringify({
            model: hasJsonInBatch ? 'gpt-4' : 'gpt-3.5-turbo', // Use GPT-4 for JSON for better accuracy
            messages: [
              {
                role: 'system',
                content: `You are a professional translator. ${hasJsonInBatch ? this.getJsonInstructions() + ' ' : ''}Translate the following numbered texts to ${request.targetLanguage}. ${this.getLanguageInstructions(request.targetLanguageCode || '')} Return ONLY the translations in the same numbered format, one per line. No explanations.`
              },
              ...(hasJsonInBatch ? [
                {
                  role: 'user',
                  content: '1. {"test": "Hello", "name": "World"}\n2. Normal text'
                },
                {
                  role: 'assistant',
                  content: '1. {"test": "Salom", "name": "Dunyo"}\n2. Oddiy matn'
                }
              ] : []),
              {
                role: 'user',
                content: batchPrompt
              }
            ],
            max_tokens: Math.min(4000, batch.length * 100), // Dynamic token limit based on batch size
            temperature: hasJsonInBatch ? 0.1 : 0.3, // Lower temperature for JSON to be more precise
          }),
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('Ошибка аутентификации OpenAI API: проверьте API ключ в настройках. Код ошибки: 401');
          }
          throw new Error(`AI Translation API error: ${response.status}`);
        }

        const data = await response.json();
        const translationsText = data.choices?.[0]?.message?.content?.trim() || '';
        
        // Parse numbered responses
        const translationLines = translationsText.split('\n').filter((line: string) => line.trim());
        
        batch.forEach((originalText, batchIndex) => {
          const globalIndex = i + batchIndex;
          
          // Find corresponding translation line
          const expectedNumber = `${globalIndex + 1}.`;
          const translationLine = translationLines.find((line: string) => 
            line.trim().startsWith(expectedNumber)
          );
          
          if (translationLine) {
            // Extract translation text after the number
            const translatedText = translationLine.replace(/^\d+\.\s*/, '').trim();
            
            results[globalIndex] = {
              translatedText,
              sourceLanguage: request.sourceLanguage || 'auto-detected',
              targetLanguage: request.targetLanguage,
              confidence: 0.9,
            };
          } else {
            // Fallback: try to get translation by position
            const fallbackTranslation = translationLines[batchIndex];
            if (fallbackTranslation) {
              const translatedText = fallbackTranslation.replace(/^\d+\.\s*/, '').trim();
              results[globalIndex] = {
                translatedText,
                sourceLanguage: request.sourceLanguage || 'auto-detected',
                targetLanguage: request.targetLanguage,
                confidence: 0.7, // Lower confidence for fallback
              };
            } else {
              errors.push({
                index: globalIndex,
                error: 'No translation found in response'
              });
            }
          }
        });

      } catch (error) {
        console.error(`Batch translation error for batch starting at ${i}:`, error);
        
        // Mark all items in this batch as failed
        batch.forEach((_, batchIndex) => {
          errors.push({
            index: i + batchIndex,
            error: error instanceof Error ? error.message : 'Batch translation failed'
          });
        });
      }

      // Add delay between batches to respect rate limits
      if (i + batchSize < request.texts.length) {
        await new Promise(resolve => setTimeout(resolve, 2000)); // Longer delay for larger batches
      }
    }

    // Fill in empty results for failed translations
    for (let i = 0; i < request.texts.length; i++) {
      if (!results[i] && !errors.find(e => e.index === i)) {
        errors.push({
          index: i,
          error: 'Translation not processed'
        });
      }
    }

    // Keep the array sparse (indexes aligned with request.texts) so callers
    // can map each translation back to its source text; compacting with
    // filter(Boolean) would shift results after a failed item onto wrong keys
    return {
      translations: results,
      totalProcessed: request.texts.length,
      errors,
    };
  }


  // Helper method to detect if text is JSON
  isJsonText(text: string): boolean {
    const trimmed = text.trim();
    // Basic structure check
    const hasJsonStructure = (trimmed.startsWith('{') && trimmed.endsWith('}')) || 
                            (trimmed.startsWith('[') && trimmed.endsWith(']'));
    
    if (!hasJsonStructure) return false;
    
    // Additional check for JSON-like content (has quotes and colons for objects)
    if (trimmed.startsWith('{')) {
      return trimmed.includes('":') && trimmed.includes('"');
    }
    
    return true; // For arrays
  }

  // Helper method to get JSON-specific instructions
  getJsonInstructions(): string {
    return `STRICT JSON RULE: Return valid JSON on ONE LINE with exact same structure. Only translate values after colons. Keep all keys, brackets, quotes, commas unchanged.`;
  }

  // Helper method to get specific language instructions for AI
  getLanguageInstructions(languageCode: string): string {
    switch (languageCode) {
      case 'uz':
        return 'IMPORTANT: Use Latin script (Latin alphabet) for Uzbek translations, NOT Cyrillic. Example: "Salom" not "Салом".';
      case 'tr':
        return 'Turkish';
      default:
        return '';
    }
  }

  // Helper method to detect if text is likely in English
  isLikelyEnglish(text: string): boolean {
    const trimmed = text.trim();
    
    // Check for common non-Latin scripts
    const chinesePattern = /[\u4e00-\u9fff]/; // Chinese characters
    const arabicPattern = /[\u0600-\u06ff]/; // Arabic characters
    const cyrillicPattern = /[\u0400-\u04ff]/; // Cyrillic characters
    
    if (chinesePattern.test(trimmed) || arabicPattern.test(trimmed) || cyrillicPattern.test(trimmed)) {
      return false;
    }
    
    // Simple check for English-like content (contains Latin letters)
    const latinPattern = /[a-zA-Z]/;
    return latinPattern.test(trimmed);
  }

  // Helper method to validate source texts before translation
  validateSourceTexts(texts: string[]): { valid: boolean; warnings: string[] } {
    const warnings: string[] = [];
    let valid = true;
    
    texts.forEach((text, index) => {
      if (!this.isLikelyEnglish(text)) {
        warnings.push(`Текст #${index + 1} не похож на английский: "${text.substring(0, 50)}..."`);
      }
    });
    
    return { valid, warnings };
  }
}

export const aiTranslationService = new AITranslationService();
export type { AITranslationRequest, AITranslationResponse, BulkAITranslationRequest, BulkAITranslationResponse };
